/* eslint-disable */
// Shared location UI for events (community + hosted): segmented venue choice,
// outside-community leaflet pin-drop picker, and the read-only map preview.
import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ExternalLink, LocateFixed } from "lucide-react";
import "./location.css";

// Default marker icon fix (CRA bundling breaks leaflet's asset URLs) — same
// pattern as security/LiveVisitorsMap.js.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const OSM_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const DEFAULT_CENTER = [17.385, 78.4867]; // community vicinity (same as security maps)
const round6 = (v) => Number(v.toFixed(6));

export const PRESET_LABELS = { central_park: "Central Park", clubhouse: "Clubhouse" };

// One-line venue text for cards/detail: preset label ("Clubhouse · Hall 2"),
// the inside detail, or the outside place name. Falls back to the legacy
// free-text venue for pre-v2 rows.
export function venueLine(location, legacyVenue) {
  if (!location) return legacyVenue || "";
  if (location.type === "outside") return location.detail || legacyVenue || "";
  const label = PRESET_LABELS[location.preset];
  if (label) return location.detail ? `${label} · ${location.detail}` : label;
  return location.detail || legacyVenue || "";
}

// Form state <-> API mapping ------------------------------------------------

// Initial form-location state from an event DTO (or empty for create).
export function locFromEvent(ev) {
  const l = ev?.location;
  if (!l) {
    // Legacy row (or create): free-text venue means "other inside".
    return ev?.venue
      ? { type: "community", preset: "other", venue: ev.venue, lat: null, lng: null }
      : { type: "community", preset: "central_park", venue: "", lat: null, lng: null };
  }
  if (l.type === "outside") {
    return { type: "outside", preset: null, venue: l.detail || "", lat: l.lat, lng: l.lng };
  }
  return {
    type: "community",
    preset: l.preset || (l.detail ? "other" : "central_park"),
    venue: l.detail || "",
    lat: null,
    lng: null,
  };
}

// The complete 5-field group the API expects (explicit nulls included).
export function locationPayload(loc) {
  const outside = loc.type === "outside";
  const venue = (loc.venue || "").trim();
  return {
    location_type: outside ? "outside" : "community",
    venue_preset: outside ? null : loc.preset,
    venue: venue || null,
    latitude: outside ? loc.lat : null,
    longitude: outside ? loc.lng : null,
  };
}

// Returns an error message, or "" when valid.
export function validateLocation(loc) {
  const venue = (loc.venue || "").trim();
  if (loc.type === "outside") {
    if (!venue) return "Place name is required for events outside the community";
    if (loc.lat == null || loc.lng == null)
      return "Drop a pin on the map (or use your current location) for the event venue";
  } else if (loc.preset === "other" && !venue) {
    return "Please describe where inside the community the event is";
  }
  return "";
}

// Map helpers ---------------------------------------------------------------

function ClickToPlace({ onPick }) {
  useMapEvents({
    click: (e) => onPick(round6(e.latlng.lat), round6(e.latlng.lng)),
  });
  return null;
}

function PanTo({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) map.panTo([lat, lng]);
  }, [lat, lng, map]);
  return null;
}

const SEGMENTS = [
  { id: "central_park", label: "Central Park" },
  { id: "clubhouse", label: "Clubhouse" },
  { id: "other", label: "Other (inside)" },
  { id: "outside", label: "Outside community" },
];

// Form section: segmented venue choice + conditional detail field / map picker.
// `loc` = { type, preset, venue, lat, lng }; `onChange` receives a partial patch.
export function LocationSection({ loc, onChange, inputClass }) {
  const [geoError, setGeoError] = useState("");
  const seg = loc.type === "outside" ? "outside" : loc.preset || "central_park";

  const pick = (id) => {
    setGeoError("");
    if (id === "outside") onChange({ type: "outside", preset: null });
    else onChange({ type: "community", preset: id, lat: null, lng: null });
  };

  const setPin = (lat, lng) => onChange({ lat, lng });

  const useMyLocation = () => {
    setGeoError("");
    if (!navigator.geolocation) {
      setGeoError("Location is not available in this browser — drop the pin manually.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setPin(round6(pos.coords.latitude), round6(pos.coords.longitude)),
      () => setGeoError("Couldn't get your location — allow location access or drop the pin manually."),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const hasPin = loc.lat != null && loc.lng != null;
  const center = hasPin ? [loc.lat, loc.lng] : DEFAULT_CENTER;

  return (
    <div className="vs-loc">
      <div className="vs-loc-seg" role="radiogroup" aria-label="Event location">
        {SEGMENTS.map((s) => (
          <button
            key={s.id}
            type="button"
            role="radio"
            aria-checked={seg === s.id}
            className={`vs-loc-seg-btn ${seg === s.id ? "active" : ""}`}
            onClick={() => pick(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {(seg === "central_park" || seg === "clubhouse") && (
        <input
          className={inputClass}
          value={loc.venue}
          onChange={(e) => onChange({ venue: e.target.value })}
          placeholder={`Extra detail (optional), e.g. ${seg === "clubhouse" ? "Hall 2" : "near the stage"}`}
        />
      )}

      {seg === "other" && (
        <input
          className={inputClass}
          value={loc.venue}
          onChange={(e) => onChange({ venue: e.target.value })}
          placeholder="Where inside the community? e.g. Community Hall 2, Villa 42 lawn"
        />
      )}

      {seg === "outside" && (
        <>
          <input
            className={inputClass}
            value={loc.venue}
            onChange={(e) => onChange({ venue: e.target.value })}
            placeholder="Place name, e.g. Taj Banjara, Road No 1"
          />
          <div className="vs-loc-map-help">
            Tap the map to drop the pin, or drag it to the exact spot.
          </div>
          <MapContainer center={center} zoom={hasPin ? 16 : 13} className="vs-loc-map">
            <TileLayer url={OSM_URL} attribution={OSM_ATTR} />
            <ClickToPlace onPick={setPin} />
            <PanTo lat={loc.lat} lng={loc.lng} />
            {hasPin && (
              <Marker
                position={[loc.lat, loc.lng]}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const p = e.target.getLatLng();
                    setPin(round6(p.lat), round6(p.lng));
                  },
                }}
              />
            )}
          </MapContainer>
          <div className="vs-loc-map-row">
            <button type="button" className="vs-loc-geo-btn" onClick={useMyLocation}>
              <LocateFixed size={14} /> Use my current location
            </button>
            {hasPin && (
              <span className="vs-loc-coords">
                {loc.lat.toFixed(5)}, {loc.lng.toFixed(5)}
              </span>
            )}
          </div>
          {geoError && <p className="vs-loc-geo-error">{geoError}</p>}
        </>
      )}
    </div>
  );
}

// Read-only preview for detail views of outside events: static map with the
// pin + "Open in Google Maps".
export function LocationPreview({ location }) {
  if (!location || location.type !== "outside" || location.lat == null || location.lng == null) {
    return null;
  }
  return (
    <div className="vs-loc-preview">
      <MapContainer
        center={[location.lat, location.lng]}
        zoom={16}
        className="vs-loc-preview-map"
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        touchZoom={false}
        boxZoom={false}
        keyboard={false}
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer url={OSM_URL} attribution={OSM_ATTR} />
        <Marker position={[location.lat, location.lng]} interactive={false} />
      </MapContainer>
      {location.maps_url && (
        <a
          className="vs-loc-gmaps"
          href={location.maps_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ExternalLink size={14} /> Open in Google Maps
        </a>
      )}
    </div>
  );
}
